import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    RoundListNavItem,
    LocationNavItem,
    RegionNavItem
} from 'common/react/components/competition-nav/nav-items';
import { BroadcastGuideCards } from './BroadcastGuideCards';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useGetRegionsQuery } from 'common/store/modules/api/content-api';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';

export const BroadcastGuide = ({ competitionId, seasonId, title }) => {
    const dispatch = useDispatch();
    const siteSettingRegion = useSingleSiteSetting('region');

    const initialValues = {
        competitionId,
        seasonId,
        location: siteSettingRegion
    };

    const nav = useSelector(navSelector);

    const options = useCompetitionNavigationOptions(nav);

    useEffect(() => {
        dispatch(initialiseNav({ initialValues: initialValues }));
    }, [dispatch]);

    const windowSize = useWindowSizeV2();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const { data = [] } = useGetRegionsQuery();

    const locations = useMemo(() => {
        if (data.length) {
            return data.reduce((regions, region) => {
                const value =
                    PULSE.app.common.broadcast.getLocationFromTimezone(
                        region.timezone
                    );

                const returnValue = {
                    name: value,
                    value,
                    extra: region
                };

                if (!regions.some((obj) => obj.name === returnValue.name)) {
                    regions.push(returnValue);
                }

                return regions.sort((textA, textB) => {
                    if (textA.name < textB.name) {
                        return -1;
                    }
                    if (textA.name > textB.name) {
                        return 1;
                    }
                    return 0;
                });
            }, []);
        }
        return [];
    }, [data]);

    const competitionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['location', 'region', 'round'],
        filtersRef: competitionNavRef
    });

    const renderMobile = () => (
        <StatsSection.NavItems>
            <LocationNavItem
                locations={locations}
                classes="bg-location-nav__item"
            />
            <RegionNavItem regions={data} classes="bg-region-nav__item" />
            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                <SettingsButton />
            </StatsSection.SettingsButton>
        </StatsSection.NavItems>
    );

    const renderDesktop = () => (
        <>
            <StatsSection.NavItems>
                <LocationNavItem
                    locations={locations}
                    className="bg-location-nav__item"
                />
                <RegionNavItem regions={data} />
            </StatsSection.NavItems>
            <StatsSection.SettingsButton>
                <SettingsButton />
            </StatsSection.SettingsButton>
        </>
    );

    return (
        <>
            <div
                className="competition-nav filter-dropdown"
                ref={competitionNavRef}
            >
                <StatsSection.Main>
                    <StatsSection.Nav
                        showResponsiveItems={showResponsiveItems}
                        withScrollableFilter={!showResponsiveItems}
                        className="broadcast-guide-nav"
                    >
                        {showResponsiveItems ? renderMobile() : renderDesktop()}

                        <StatsSection.RoundButtons>
                            <RoundListNavItem
                                classes={
                                    'competition-nav__round-filter-wrapper'
                                }
                            />
                        </StatsSection.RoundButtons>
                    </StatsSection.Nav>
                </StatsSection.Main>
            </div>
            <div className="wrapper widget">
                <BroadcastGuideCards
                    competitionId={competitionId}
                    seasonId={seasonId}
                />
            </div>
        </>
    );
};

BroadcastGuide.propTypes = {
    competitionId: PropTypes.string.isRequired,
    seasonId: PropTypes.string.isRequired,
    title: PropTypes.string
};
